import { useState } from "react";
import { Alert, Col, Descriptions, Form, Input, InputNumber, Modal, Row, notification } from "antd";
import { generateInvoicePdf } from "../../services/contractsAPI";
import { getChangeOrderLabel } from "../../utils/projectUtils";

const billingFields = [
  ["previously_invoiced", "Previously invoiced (excluding this invoice)"],
  ["this_invoice", "This invoice"],
  ["total_invoiced", "Total invoiced (including this invoice)"],
  ["remaining_to_invoice", "Remaining to invoice"],
];
const required = [{ required: true, message: "Please fill out this field." }];
const requiredText = [{ required: true, whitespace: true, message: "Please fill out this field." }];
const currency = (value) => Number(value || 0).toLocaleString("en-US", {
  style: "currency", currency: "USD",
});

export default function GenerateInvoiceModal({ contract, project, onClose }) {
  const [form] = Form.useForm();
  const [generating, setGenerating] = useState(false);
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  const generate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setGenerating(true);
    try {
      const pdf = await generateInvoicePdf(project.project_id, contract._id || contract.id, values);
      const url = URL.createObjectURL(pdf);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${values.number.replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      notification.success({ message: "Invoice PDF generated" });
    } catch (error) {
      let message = "Could not generate the invoice. Please try again.";
      try {
        const body = error.response?.data;
        const data = body instanceof Blob ? JSON.parse(await body.text()) : body;
        if (typeof data?.detail === "string") message = data.detail;
        else if (Array.isArray(data?.detail)) message = data.detail.map((item) => item.msg).join("; ");
      } catch { /* Use the fallback message for non-JSON responses. */ }
      notification.error({ message: "Invoice generation failed", description: message });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal
      open
      title="Generate invoice"
      width={760}
      okText="Download PDF"
      confirmLoading={generating}
      onOk={generate}
      onCancel={onClose}
      cancelButtonProps={{ disabled: generating }}
      closable={!generating}
      maskClosable={false}
      keyboard={!generating}
    >
      <Alert
        type="info"
        showIcon
        message="Enter your billing amounts from your own records."
        description="All four billing amounts are printed as entered. Invoice details are not saved; download the PDF before closing this form."
        className="mb-4"
      />
      <Descriptions size="small" bordered column={1} className="mb-4">
        <Descriptions.Item label="Contract">#{contract.number} — {contract.title}</Descriptions.Item>
        <Descriptions.Item label="Original contract amount">{currency(contract.original_contract_amount)}</Descriptions.Item>
        <Descriptions.Item label={`Approved ${getChangeOrderLabel(project, true)} (current)`}>{currency(contract.approved_change_orders)}</Descriptions.Item>
        <Descriptions.Item label="Revised contract amount">{currency(contract.revised_contract_amount)}</Descriptions.Item>
      </Descriptions>
      <Form form={form} layout="vertical" initialValues={{
        invoice_date: today,
        bill_to: contract.contract_company?.name || "",
        description: contract.title || "",
        billing_address: "",
        notes: "",
      }}>
        <Row gutter={16}>
          <Col xs={24} sm={8}><Form.Item name="number" label="Invoice number" rules={requiredText}><Input maxLength={30} /></Form.Item></Col>
          <Col xs={24} sm={8}><Form.Item name="invoice_date" label="Invoice date" rules={required}><Input type="date" /></Form.Item></Col>
          <Col xs={24} sm={8}><Form.Item name="due_date" label="Due date" dependencies={["invoice_date"]} rules={[
            ...required,
            ({ getFieldValue }) => ({ validator(_, value) {
              return value && value < getFieldValue("invoice_date")
                ? Promise.reject(new Error("Due date must be on or after the invoice date.")) : Promise.resolve();
            } }),
          ]}><Input type="date" /></Form.Item></Col>
          <Col xs={24} sm={12}><Form.Item name="period_start" label="Billing period start" rules={required}><Input type="date" /></Form.Item></Col>
          <Col xs={24} sm={12}><Form.Item name="period_end" label="Billing period end" dependencies={["period_start"]} rules={[
            ...required,
            ({ getFieldValue }) => ({ validator(_, value) {
              return value && value < getFieldValue("period_start")
                ? Promise.reject(new Error("Period end must be on or after its start.")) : Promise.resolve();
            } }),
          ]}><Input type="date" /></Form.Item></Col>
        </Row>
        <Form.Item name="bill_to" label="Bill to" rules={requiredText}><Input maxLength={150} /></Form.Item>
        <Form.Item name="billing_address" label="Billing address"><Input.TextArea rows={2} maxLength={400} /></Form.Item>
        <Form.Item name="description" label="Description of work being billed" rules={requiredText}><Input.TextArea rows={3} maxLength={1200} /></Form.Item>
        <Row gutter={16}>
          {billingFields.map(([name, label]) => (
            <Col xs={24} sm={12} key={name}>
              <Form.Item name={name} label={label} rules={required}>
                <InputNumber stringMode precision={2} max="999999999999.99" min="-999999999999.99" controls={false} prefix="$" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          ))}
        </Row>
        <Form.Item name="notes" label="Notes / payment instructions"><Input.TextArea rows={3} maxLength={1200} /></Form.Item>
      </Form>
    </Modal>
  );
}
